// File path: /shell/src/routing/landingPath.ts
// Where the shell sends a signed-in user who opens "/" (D-D, S4 row 8; Ruling 24b).
//
// In order:
//   1. a role Navigate serves, in an organization that has Navigate → that role's Navigate page;
//   2. a SpEd role — one Bridge serves that neither Navigate nor the Elevate fallback serves —
//      in an organization that has Bridge → that role's Bridge page;
//   3. everyone else → the Elevate path the old redirect chose.
// This is convenience only — the authorizer, not the UI, enforces roles and subscriptions
// (contracts §1). A role nothing serves still gets the Elevate path; ModuleLoader shows its
// Access Denied view there instead of bouncing.
//
// The module side is read from each module's own entry (getDefaultRoute + routes), so this file
// never restates which roles a module serves. The landing is the role's full page (e.g.
// /navigate/coordinator), and only a page whose allowedRoles admit the role.

import { getDefaultRoute as navigateDefaultRoute, routes as navigateRoutes } from '@modules/navigate/module';
import { getDefaultRoute as bridgeDefaultRoute, routes as bridgeRoutes } from '@modules/bridge/module';
import type { ModuleId } from '@core/config/moduleRegistry';
import type { ModuleRouteConfig } from '@core/types/module';

interface LandingModule {
  id: ModuleId;
  getDefaultRoute: (role: string) => string;
  routes: ModuleRouteConfig[];
}

const NAVIGATE: LandingModule = { id: 'navigate', getDefaultRoute: navigateDefaultRoute, routes: navigateRoutes };
const BRIDGE: LandingModule = { id: 'bridge', getDefaultRoute: bridgeDefaultRoute, routes: bridgeRoutes };

/** Every role any of the module's routes admits. */
const rolesServedBy = (m: LandingModule): string[] =>
  Array.from(new Set(m.routes.flatMap((r) => r.allowedRoles ?? [])));

// The old redirect's hierarchy, highest first, moved here unchanged: the roles the Elevate
// fallback serves. Its Elevate paths are /elevate/<lowercased role>.
const ELEVATE_ROLES = ['SuperAdmin', 'StateAdmin', 'ProgramAdmin', 'DistrictAdmin', 'SchoolAdmin', 'Coach', 'Teacher'];

// Today: module.ts TEACHER_ROLES.
const NAVIGATE_ROLES = rolesServedBy(NAVIGATE);

const sameRole = (a: string) => (b: string) => a.toLowerCase() === b.toLowerCase();

// Bridge roles the other two do not serve — today SpEdTeacher, SchoolPsychologist, SpEdDirector.
// (ProgramAdmin and StateAdmin are Bridge roles too, but the Elevate fallback serves them.)
const SPED_ROLES = rolesServedBy(BRIDGE)
  .filter((r) => !NAVIGATE_ROLES.some(sameRole(r)) && !ELEVATE_ROLES.some(sameRole(r)));

// Which group decides when several match: the old hierarchy with Navigate's own roles
// slotted in below SchoolAdmin. Any other known role (the SpEd roles) ranks last.
const ROLE_PRECEDENCE = [
  'SuperAdmin', 'StateAdmin', 'ProgramAdmin', 'DistrictAdmin', 'SchoolAdmin',
  'MTSSCoordinator', 'Counselor', 'Coach', 'Teacher',
];

const KNOWN_ROLES = Array.from(new Set([...ROLE_PRECEDENCE, ...NAVIGATE_ROLES, ...SPED_ROLES]));

// The modules a landing can go to before the Elevate fallback, in order, with the roles each takes.
const LANDINGS: Array<[LandingModule, string[]]> = [[NAVIGATE, NAVIGATE_ROLES], [BRIDGE, SPED_ROLES]];

const lower = (s: string) => s.toLowerCase();

/** The canonical spelling of a role name from `among`, matched case-insensitively. */
const match = (name: string | null | undefined, among: readonly string[]): string | undefined =>
  name ? among.find((r) => lower(r) === lower(name)) : undefined;

const rank = (role: string): number => {
  const i = ROLE_PRECEDENCE.indexOf(role);
  return i === -1 ? ROLE_PRECEDENCE.length : i;
};

/** The highest-ranked group that names one of `among`. */
const bestGroup = (groups: readonly string[] | null | undefined, among: readonly string[]): string | undefined =>
  (groups ?? [])
    .map((g) => match(g, among))
    .filter((r): r is string => r !== undefined)
    .sort((a, b) => rank(a) - rank(b))[0];

/**
 * The user's actual role, in its canonical spelling: `role` when it is a role LumiLearn knows
 * (Elevate's, Navigate's or a SpEd role; any case), otherwise the highest such group (the old
 * redirect's "groups as fallback"), otherwise undefined. ModuleLoader uses it too, so a module tab
 * and sign-in pick the same page.
 */
export const effectiveRole = (
  role: string | null | undefined,
  groups: readonly string[] | null | undefined,
): string | undefined => match(role, KNOWN_ROLES) ?? bestGroup(groups, KNOWN_ROLES);

/** The module's page for `role`, when the module's landing route for that role admits it. */
const modulePath = (m: LandingModule, role: string): string | undefined => {
  const page = m.getDefaultRoute(role);
  const route = m.routes.find((r) => r.path === page);
  if (!route) return undefined;
  const allowed = route.allowedRoles ?? [];
  return allowed.length === 0 || match(role, allowed) ? `/${m.id}/${page}` : undefined;
};

/** Exactly the old redirect: role first, then groups, then 'teacher'. */
const elevatePath = (role: string | null | undefined, groups: readonly string[] | null | undefined): string => {
  const elevateRole = match(role, ELEVATE_ROLES) ?? bestGroup(groups, ELEVATE_ROLES) ?? 'Teacher';
  // Defensive only: elevateRole is always one of ELEVATE_ROLES, so /login is unreachable (as
  // it was in the old redirect's switch default).
  return ELEVATE_ROLES.includes(elevateRole) ? `/elevate/${lower(elevateRole)}` : '/login';
};

/**
 * Where to send a signed-in user who opens "/".
 *
 * @param role            `user.role` (from custom:role), any case
 * @param groups          the user's Cognito groups, any case
 * @param hasModuleAccess the subscription check (SubscriptionContext)
 */
export function landingPath(
  role: string | null | undefined,
  groups: readonly string[] | null | undefined,
  hasModuleAccess: (moduleId: ModuleId) => boolean,
): string {
  const resolved = effectiveRole(role, groups);
  if (resolved) {
    for (const [module, roles] of LANDINGS) {
      if (match(resolved, roles) && hasModuleAccess(module.id)) {
        const path = modulePath(module, resolved);
        if (path) return path;
      }
    }
  }
  return elevatePath(role, groups);
}
